import { tryOnMounted, useEventBus } from '@vueuse/core'
import { getAssetsPath } from '@/utils/utils'
import {
  OrderCommonStatus,
  OrderPlatformType,
  OrderMovieStatus,
  AttendeeFormStatus,
  OrderGroupRole,
  type MovieOrder,
  type OrderDetail,
  type OrderListItem,
} from '@/view-models/order'
import { ProductSalesMode } from '@/view-models/product'
import * as OrderService from '@/service/order'
import { usePlatformStore } from '@/store-pinia/platform'
import { ResponseCode } from '@/view-models/common'
import { LoginDialogTrigger } from '@/view-models/account'
import { LoginEvent } from '@/view-models/event-bus'

export function useOrderFlags(orderParam: MaybeRef<OrderDetail>) {
  const order = computed<OrderDetail>(() => unref(orderParam) || ({} as OrderDetail))
  const orderStatusPayload = reactive({
    isMovie: computed(() => !!order.value.movie),
    showingRemark: computed(() => !!(order.value.remarks?.length || order.value.member_note)),
    isCamping: computed(() => order.value.sales_mode === ProductSalesMode.Camping),
    isProductHasSubitem: computed(() => !!order.value.product?.sub_items?.length),
    showProductsQty: computed(() => 'product' in order.value && !order.value.upgrades?.length),
    isPaidOnsite: computed(() => !!order.value.is_paid_onsite),
    isCancel: computed(() => order.value.order_status === OrderCommonStatus.Cancel || order.value.movie?.status === OrderMovieStatus.Cancel),
    isFromFunBook: computed(() => order.value.type === OrderPlatformType.FunBookV2),
    isDelivery: computed(() => !!order.value.delivery),
    isOrderFormAlertShown: computed(() => order.value.attendee_status === AttendeeFormStatus.Unfinished),
    isGroupingOrder: computed(
      () => order.value.orders_group_role === OrderGroupRole.GroupOrderOwner || order.value.orders_group_role === OrderGroupRole.GroupOrderFollower
    ),
    isDeferPayment: computed(() => order.value.is_defer_payment),
    isPaymentPaid: computed(() => order.value.has_paid),
    isPaymentExpired: computed(() => order.value.is_payment_expired),
    isOrderHeadcountChangeable: computed(() => order.value.headcount_changeable),
    isBookSeatOnly: computed(() => !!order.value.is_seat_only),
    hasSeat: computed(() => !!order.value.seat?.id),
  })

  return {
    ...toRefs(orderStatusPayload),
  }
}

export function useOrderDetailItemData(order: Ref<any>) {
  const { $i18n } = useNuxtApp()
  const { isMovie, isCamping, isProductHasSubitem } = useOrderFlags(order)

  /**  目前沒有設計主商品有子選項但不是露營的 wording，所以先回傳 booking_detail_pax */
  const itemAmountLabel = computed(() =>
    isProductHasSubitem.value ? (isCamping.value ? $i18n.t('tent_count') : $i18n.t('booking_detail_pax')) : $i18n.t('booking_detail_pax')
  )
  const itemAmount = computed(() => {
    if (isMovie.value) {
      return order.value.movie.cust_num
    }
    return isProductHasSubitem.value ? order.value.product.qty : order.value.occupation_count || ''
  })

  const productSubitemList = computed(() =>
    isProductHasSubitem.value
      ? order.value.product?.sub_items?.map((productSubitems: any[], index: number) => ({
          titleLabel: getSubitemTitle(index + 1),
          subitemLabels: productSubitems.map(item => `${item.name} x ${item.qty}`),
        }))
      : []
  )

  const txnText = computed(() => `${order.value.symbol_code} ${order.value.txn_amount?.toLocaleString()}`)

  function getSubitemTitle(index: number) {
    return `1 x ${order.value.product.name} ${index}`
  }

  return {
    itemAmount,
    itemAmountLabel,
    productSubitemList,
    txnText,
  }
}

export function useMovieOrderProduct(movieOrder: Ref<any>) {
  const { $i18n } = useNuxtApp()

  const movieSeatsString = computed(() => $i18n.t('movie_room_number', [movieOrder.value.hall]) + ` | ` + movieOrder.value.seats?.join(', '))

  const productCtx = reactive({
    name: computed(() => movieOrder.value.name),
    version: computed(() => movieOrder.value.version),
    cinemaName: computed(() => movieOrder.value.cinema_name),
    image: computed(() => getAssetsPath(movieOrder.value.poster)),
  })

  return {
    movieSeatsString,
    productCtx,
  }
}

export function useMovieOrderContent(movieOrder: Ref<any>) {
  const ticketsInfo = computed<MovieOrder[]>(() => movieOrder.value?.tickets || [])
  const originTicketsInfo = computed<MovieOrder[]>(() => movieOrder.value?.ori_tickets || [])

  return {
    ticketsInfo,
    originTicketsInfo,
  }
}

export function useOrderDetailContext() {
  const route = useRoute()
  const { $store: store } = useNuxtApp()

  const orderID = computed(() => route.params.order_id as string)
  const fromTngMiniProgram = computed(() => store.getters.fromTngMiniProgram)
  const token = computed(() => (route.query.t || '') as string)

  return {
    orderID,
    fromTngMiniProgram,
    token,
  }
}

export function useOrderDetailPage() {
  const { orderID, fromTngMiniProgram, token } = useOrderDetailContext()
  const { order, getOrderDetail } = useOrderDetailData(orderID, fromTngMiniProgram, token)

  return {
    order,
    getOrderDetail,
  }
}

export function useOrderPaths(branchId = ref(''), orderId = ref(''), token = ref('')) {
  const platformStore = usePlatformStore()
  const { $store: store } = useNuxtApp()

  const platformPathPrefix = computed(() => (platformStore.isFunBookV2 ? `/funbook/${branchId.value}` : ``))

  const orderPathMap = computed(() => ({
    orderDetail: store.state.foreignDomainFromMichelin
      ? { path: `/orders/michelin` }
      : {
          path: `${platformPathPrefix.value}/orders/info/${orderId.value}`,
          query: token.value ? { t: token.value } : {},
        },
  }))

  return {
    orderPathMap,
  }
}

export function useOrderDetailData(orderID = ref(''), checkStatus = ref(false), token = ref('')) {
  const { $store: store, $axios, $localePath } = useNuxtApp()
  const router = useRouter()
  const order = ref<OrderDetail>({ product: {}, order_status: 0, cs_channel: '' } as any)
  const loginBus = useEventBus(LoginEvent)

  tryOnMounted(async () => {
    try {
      if (checkStatus.value) {
        await getOrderStatus()
      }
      await getOrderDetail()
    } catch (error) {
      console.error(error)
    }

    loginBus.on((isLogin, _from) => {
      if (!isLogin) {
        router.push($localePath('/'))
        return
      }
      getOrderDetail()
    })
  })

  async function getOrderDetail() {
    try {
      const res = await OrderService.getOrderDetail($axios as any, { orderID: orderID.value, token: token.value })
      if (res.code !== 0) {
        throw res
      }
      // sales_mode: 4 外送酒訂單
      if (res.data.sales_mode === ProductSalesMode.WineDelivery) {
        router.replace($localePath({ name: 'orders-list' }))
        return
      }
      order.value = res.data
    } catch (error: any) {
      if (error?.code === ResponseCode.Unauthorized) {
        store.dispatch('dialog/setLoginDialog', { state: true, options: { from: LoginDialogTrigger.OrderDetail } })
        return
      }
      store.dispatch('dialog/setRootMessageOption', {
        status: true,
        options: {
          message: error.message,
          confirm: () => {
            if (error?.code === ResponseCode.TokenNotFound) {
              router.push($localePath('/'))
            }
          },
        },
      })
    }
  }

  async function getOrderStatus() {
    try {
      const res = await OrderService.getOrderStatus($axios as any, { orderID: orderID.value })
      if (res.code !== 0) {
        throw res
      }
    } catch (error) {
      console.error('getOrderStatus Error:', error)
      throw error
    }
  }

  return {
    order,
    getOrderDetail,
  }
}
